import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LDOptions, LDProvider, useLDClient, LDEvaluationDetail } from "launchdarkly-react-client-sdk";
import { buildLaunchDarklyContext, setLaunchDarklyClient } from "../../clients/launchDarkly";
import type { UserProfile } from "../../interfaces/User";
import Logger from "../../clients/sentry";
import { datadogRum } from "@datadog/browser-rum";
import { browserStore } from "../../utils/storageKeys";

const LAUNCHDARKLY_CLIENT_ID_PROD = "67815f5070c9a309ca9c8bcc";
const LAUNCHDARKLY_RELAY_PROD = "https://ldrelay.ediphi.com";
const LAUNCHDARKLY_CLIENT_ID_STAGE = "6785764328826e099e5d2967";
const LAUNCHDARKLY_RELAY_STAGE = "https://ldrelay.stage.ediphi.com";

function getLaunchDarklyConfig(): { clientSideId: string; relayUrl: string } {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  // Localhost talks to the Non-Production LD environment so flags toggled there
  // (the only env devs typically have access to) take effect during local development.
  const isNonProd = hostname === "excel.stage.ediphi.com" || hostname === "localhost";
  return {
    clientSideId: isNonProd ? LAUNCHDARKLY_CLIENT_ID_STAGE : LAUNCHDARKLY_CLIENT_ID_PROD,
    relayUrl: isNonProd ? LAUNCHDARKLY_RELAY_STAGE : LAUNCHDARKLY_RELAY_PROD,
  };
}

function getStoredUserProfile(): UserProfile | undefined {
  try {
    const raw = browserStore.get("userProfile");
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return undefined;
    const id = typeof parsed.id === "string" ? parsed.id : "";
    const email = typeof parsed.email === "string" ? parsed.email : undefined;
    const full_name = typeof parsed.full_name === "string" ? parsed.full_name : undefined;
    const admin = parsed.admin === true;
    return { id: id || email || "anonymous", email, full_name, admin };
  } catch {
    return undefined;
  }
}

// The LD context is built once from localStorage when the provider mounts — which is before
// login, so on a fresh session it carries the anonymous defaults (tenant "excel", no user).
// Login stores the tenant + profile in localStorage but does not reload the page, so without an
// explicit re-identify the SDK keeps evaluating flags against those defaults for the whole
// session, silently hiding tenant/user-gated features until a full restart. Callers invoke this
// after login to re-identify with the now-current localStorage values.
type IdentifyLaunchDarkly = () => Promise<void>;
const LaunchDarklyIdentifyContext = React.createContext<IdentifyLaunchDarkly>(async () => {});
export const useLaunchDarklyIdentify = (): IdentifyLaunchDarkly => useContext(LaunchDarklyIdentifyContext);

function LDClientAwaiter({ children }: { children: JSX.Element }) {
  const [ldInitialized, setLdInitialized] = useState(false);
  const client = useLDClient();
  const initializeRef = useRef(false);

  const identify = useCallback<IdentifyLaunchDarkly>(async () => {
    if (!client) return;
    try {
      await client.identify(buildLaunchDarklyContext(getStoredUserProfile()));
    } catch (err) {
      Logger.warn(`LaunchDarkly identify failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [client]);

  useEffect(() => {
    if (!client || initializeRef.current) return;

    initializeRef.current = true;
    let isMounted = true;

    (async () => {
      setLaunchDarklyClient(client);

      try {
        await client.waitForInitialization(2);
      } catch (err) {
        if (err instanceof Error) Logger.info(`LaunchDarkly failed to initialize: ${err.message}.`);
      } finally {
        if (isMounted) {
          setLdInitialized(true);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [client, ldInitialized]);

  return (
    <LaunchDarklyIdentifyContext.Provider value={identify}>
      <div style={{ visibility: ldInitialized ? "visible" : "hidden", height: "100%", width: "100%" }}>{children}</div>
    </LaunchDarklyIdentifyContext.Provider>
  );
}

export function LaunchDarklyProvider({ children }: { children: JSX.Element }): JSX.Element {
  const [userProfile] = useState<UserProfile | undefined>(getStoredUserProfile);

  const { clientSideId, relayUrl } = useMemo(() => getLaunchDarklyConfig(), []);

  const context = useMemo(() => buildLaunchDarklyContext(userProfile), [userProfile]);

  const options: LDOptions = useMemo(() => {
    return {
      baseUrl: relayUrl,
      streamUrl: relayUrl,
      eventsUrl: relayUrl,
      streaming: true,
      inspectors: [
        {
          type: "flag-used",
          name: "dd-inspector",
          method: (key: string, detail: LDEvaluationDetail) => {
            if (typeof detail.value === "object" && detail.value !== null) {
              Object.entries(detail.value).forEach(([subKey, subValue]) => {
                datadogRum.addFeatureFlagEvaluation(`${key}_${subKey}`, subValue);
              });
            } else {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            }
          },
        },
      ],
    };
  }, [relayUrl]);

  return (
    <LDProvider
      clientSideID={clientSideId}
      context={context}
      options={options}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      <LDClientAwaiter>{children}</LDClientAwaiter>
    </LDProvider>
  );
}
